import { Component, Input, signal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Product } from 'src/app/models/core/product.model';
import { FormControl } from '@angular/forms';
import { TicketActionsService } from 'src/app/services/ticket/ticket-actions.service';
import { Ticket } from 'src/app/models/core/ticket.model';
import { autoHeightSheetOptions } from 'src/app/utils/auto-height-sheet';
import { getUnitPrice, hasPrice } from 'src/app/utils/product-price';
import { ShareDirectTicketModalComponent } from '../share-direct-ticket-modal/share-direct-ticket-modal.component';

export interface GenerateTicketModalOptions {
  readonly onViewInfo?: () => void | Promise<void>;
}

@Component({
  selector: 'app-generate-ticket-modal',
  templateUrl: './generate-ticket-modal.component.html',
  styleUrls: ['./generate-ticket-modal.component.scss'],
  standalone: false
})
export class GenerateTicketModalComponent {
  @Input() product!: Product;
  /** Solo en venta directa: abre la info del producto sin salir de este sheet. */
  @Input() onViewInfo?: () => void | Promise<void>;
  public referenceControl = new FormControl('');
  public quantity = signal(1);
  public isGenerating = signal(false);

  static async open(modalCtrl: ModalController, product: Product, options: GenerateTicketModalOptions = {}): Promise<void> {
    const { onViewInfo } = options;
    const modal = await modalCtrl.create({
      component: GenerateTicketModalComponent,
      componentProps: onViewInfo ? { product, onViewInfo } : { product },
      ...autoHeightSheetOptions(),
    });
    await modal.present();
  }

  /** `price` es opcional en el modelo: sin precio, el producto es sin cargo. */
  get unitPrice(): number {
    return getUnitPrice(this.product);
  }

  get hasPrice(): boolean {
    return hasPrice(this.product);
  }

  get total(): number {
    return this.unitPrice * this.quantity();
  }

  constructor(
    private modalCtrl: ModalController,
    private ticketActionsService: TicketActionsService,
  ) { }

  get productLimit(): number {
    return this.product?.limit_quote > 0 ? this.product.limit_quote : 10;
  }

  closeModal() {
    this.modalCtrl.dismiss();
  }

  async onSubmitGenerateTicket() {
    this.isGenerating.set(true);
  
    try {
      const createTicketData = {
        product_id: this.product.id,
        quantity_persons: this.quantity(),
        payment_status: 1,
        payment_method: 'cash',
        reference: this.referenceControl.value ?? '',
      };
  
      const ticket = await this.ticketActionsService.generateTicket(createTicketData);
      this.modalCtrl.dismiss();
      this.shareTicket(ticket);
    } catch (error) {
      console.error('Error generating ticket:', error);
    } finally {
      this.isGenerating.set(false);
    }
  }

  async shareTicket(ticket: Ticket) {
    await this.modalCtrl.dismiss();

    const initialBreakpoint = 0.65;

    const modal = await this.modalCtrl.create({
      component: ShareDirectTicketModalComponent,
      componentProps: {
        ticket,
      },
      breakpoints: [0, 0.5, 0.75, 0.95],
      initialBreakpoint,
      cssClass: 'custom-modal-options',
      handle: true,
    });

    await modal.present();
  }

}
